import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axiosInstance from "../../utils/axios/axiosConfig";
import { toast } from "react-toastify";
import { DeleteModal } from "../../components";
import { MagnifyingGlass, Plus, PencilSimple, Trash, ShieldCheck } from "@phosphor-icons/react";
import {
  PageHeader, Button, Input, Table, THead, TBody, TR, TH, TD,
  Skeleton, EmptyState, Pagination, Tooltip,
} from "../../components/ui";

function Roles() {
  const [loading, setLoading] = useState(false);
  const [roles, setRoles] = useState([]);
  const [roleId, setRoleId] = useState(null);
  const [done, setDone] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const rolesPerPage = 10;

  useEffect(() => {
    getRoles();
  }, [done]);

  const getRoles = async () => {
    try {
      setLoading(true);
      const res = await axiosInstance.get("/role/search");
      setRoles(res?.data?.data || []);
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to fetch roles");
    } finally {
      setLoading(false);
    }
  };

  const filteredRoles = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return roles;
    return roles.filter(
      (r) => r.name?.toLowerCase().includes(q) || r.description?.toLowerCase().includes(q)
    );
  }, [roles, searchTerm]);

  const indexOfLastRole = currentPage * rolesPerPage;
  const indexOfFirstRole = indexOfLastRole - rolesPerPage;
  const currentRoles = filteredRoles.slice(indexOfFirstRole, indexOfLastRole);
  const totalPages = Math.max(1, Math.ceil(filteredRoles.length / rolesPerPage));

  return (
    <div className="w-full p-6 text-start">
      <PageHeader
        title="Roles"
        subtitle="Define roles and the permissions attached to them."
        actions={
          <>
            <Input
              icon={MagnifyingGlass}
              placeholder="Search roles…"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
              containerClassName="w-full sm:w-64"
            />
            <Link to="/Roles/new">
              <Button icon={Plus}>Add role</Button>
            </Link>
          </>
        }
      />

      <Table>
        <THead sticky>
          <TR className="hover:bg-transparent">
            <TH>Name</TH>
            <TH>Description</TH>
            <TH className="text-right">Actions</TH>
          </TR>
        </THead>
        <TBody>
          {loading &&
            Array.from({ length: 5 }).map((_, i) => (
              <TR key={i} className="hover:bg-transparent">
                <TD><Skeleton className="h-3.5 w-28" /></TD>
                <TD><Skeleton className="h-3.5 w-64" /></TD>
                <TD><Skeleton className="ml-auto h-7 w-16" /></TD>
              </TR>
            ))}

          {!loading &&
            currentRoles.map((role) => (
              <TR key={role.id}>
                <TD className="font-medium text-ink">{role.name}</TD>
                <TD className="text-muted">{role.description || "—"}</TD>
                <TD>
                  <div className="flex items-center justify-end gap-0.5">
                    <Tooltip label="Edit">
                      <Link to={`/Roles/update/${role.id}`} className="grid h-8 w-8 place-items-center rounded-lg text-muted transition hover:bg-surface-2 hover:text-ink">
                        <PencilSimple size={16} />
                      </Link>
                    </Tooltip>
                    <Tooltip label="Delete">
                      <button
                        onClick={() => {
                          setRoleId(role.id);
                          setDeleteModal(true);
                        }}
                        className="grid h-8 w-8 place-items-center rounded-lg text-muted transition hover:bg-red-500/10 hover:text-red-600"
                      >
                        <Trash size={16} />
                      </button>
                    </Tooltip>
                  </div>
                </TD>
              </TR>
            ))}

          {!loading && currentRoles.length === 0 && (
            <tr>
              <td colSpan={3}>
                <EmptyState
                  icon={ShieldCheck}
                  title="No roles found"
                  description="Create a role to start assigning permissions to your team."
                  action={
                    <Link to="/Roles/new">
                      <Button icon={Plus}>Add role</Button>
                    </Link>
                  }
                />
              </td>
            </tr>
          )}
        </TBody>
      </Table>

      <Pagination
        className="mt-4"
        page={currentPage}
        totalPages={totalPages}
        onChange={(p) => setCurrentPage(p)}
        summary={`Showing ${filteredRoles.length ? indexOfFirstRole + 1 : 0}–${Math.min(indexOfLastRole, filteredRoles.length)} of ${filteredRoles.length} roles`}
      />

      <DeleteModal
        setDeleteId={setRoleId}
        open={deleteModal}
        setOpen={setDeleteModal}
        DeleteId={roleId}
        Model="role"
        setDone={setDone}
        setLoding={setLoading}
        done={done}
      />
    </div>
  );
}

export default Roles;
